import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useEffect, useRef, type Ref } from "react";
import type { DraftedFixResult, ModelAssistInput } from "../../api/model.js";
import type {
  FindingAssigneeOption,
  FindingQueryState,
  FindingSortKey,
  FindingView,
  RemediationProposal,
} from "../../api/types.js";
import { Badge, ReplayBadge, SeverityBadge, StatusBadge } from "../../components/Badge.js";
import { formatDateTime, formatPercent } from "../../lib/format.js";
import { displayRuleId } from "../../app/rule-id.js";
import { ANALYSIS_FAMILY_LABELS } from "./model.js";

export type ColumnKey =
  | "severity"
  | "status"
  | "validation"
  | "repository"
  | "path"
  | "rule"
  | "confidence"
  | "replay"
  | "owner"
  | "updated";

export const COLUMN_DEFINITIONS: readonly { key: ColumnKey; label: string }[] = [
  { key: "severity", label: "Severity" },
  { key: "status", label: "Status" },
  { key: "validation", label: "Verdict" },
  { key: "repository", label: "Repository" },
  { key: "path", label: "Location" },
  { key: "rule", label: "Rule" },
  { key: "confidence", label: "Confidence" },
  { key: "replay", label: "Replay" },
  { key: "owner", label: "Owner / SLA" },
  { key: "updated", label: "Updated" },
];

export const DEFAULT_VISIBLE_COLUMNS: readonly ColumnKey[] = [
  "severity",
  "status",
  "validation",
  "confidence",
  "owner",
];

const FINDING_ROW_CONTROL_SELECTOR = "button, input, a, select, textarea, [role='button']";

export function isFindingRowControl(target: EventTarget | null): boolean {
  if (!target || typeof (target as { closest?: unknown }).closest !== "function") return false;
  return Boolean((target as Element).closest(FINDING_ROW_CONTROL_SELECTOR));
}

export function assigneeDisplayName(principalId: string): string {
  if (principalId.includes("@"))
    return principalId.slice(0, principalId.indexOf("@")) || principalId;
  return principalId.split(/[/:]/u).at(-1) ?? principalId;
}

export function assigneeInitials(principalId: string): string {
  const name = assigneeDisplayName(principalId);
  const bits = name.split(/[._\-\s]/u).filter(Boolean);
  if (bits.length >= 2) return `${bits[0]!.slice(0, 1)}${bits[1]!.slice(0, 1)}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function defaultAssignmentDueAt(now = Date.now()): string {
  return new Date(now + 7 * 24 * 60 * 60 * 1000).toISOString();
}

export function FindingAssigneePicker({
  finding,
  assignees,
  pending = false,
  onAssign,
}: {
  finding: FindingView;
  assignees: readonly FindingAssigneeOption[];
  pending?: boolean;
  onAssign: (finding: FindingView, assigneeId: string | null) => void;
}) {
  const current = finding.assignment?.assigneeId ?? "";
  return (
    <label className="finding-assignee">
      <span className="finding-assignee__avatar" aria-hidden="true">
        {current ? assigneeInitials(current) : "·"}
      </span>
      <select
        aria-label={`Assign ${finding.title}`}
        value={current}
        disabled={pending || assignees.length === 0}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => onAssign(finding, event.target.value || null)}
      >
        <option value="">Unassigned</option>
        {assignees.map((assignee) => (
          <option key={assignee.principalId} value={assignee.principalId}>
            {assigneeDisplayName(assignee.principalId)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function findingQueueMeta(finding: FindingView): string {
  const family =
    finding.analysisFamily && finding.analysisFamily in ANALYSIS_FAMILY_LABELS
      ? ANALYSIS_FAMILY_LABELS[finding.analysisFamily]
      : "SOURCE";
  return `${family} · ${finding.location.path}:${finding.location.lineStart} · ${displayRuleId(finding.ruleId)}`;
}

function SelectionCheckbox({
  checked,
  mixed = false,
  label,
  onChange,
}: {
  checked: boolean;
  mixed?: boolean;
  label: string;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  }, [mixed]);
  return (
    <label className="selection-control">
      <input ref={ref} type="checkbox" checked={checked} aria-label={label} onChange={onChange} />
    </label>
  );
}

function SortHeader({
  label,
  sortKey,
  state,
  onSort,
  className,
}: {
  label: string;
  sortKey: FindingSortKey;
  state: FindingQueryState;
  onSort: (sort: FindingSortKey) => void;
  className?: string;
}) {
  const active = state.sort === sortKey;
  const Icon = active ? (state.direction === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      className={className}
      aria-sort={active ? (state.direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button className="table-sort" type="button" onClick={() => onSort(sortKey)}>
        {label}
        <Icon aria-hidden="true" size={13} />
      </button>
    </th>
  );
}

/**
 * Model assistance, threaded rather than fetched here.
 *
 * `explanations` is keyed by finding id and its values are `unknown` on purpose:
 * each is a `model_artifacts` payload that originated outside the trust
 * boundary, and it is handed to `ModelExplanationPanel` exactly as the API sent
 * it. Nothing on the way down reshapes it, reads a field of it, or decides
 * whether it is model-generated -- the renderer's marker is a constant it emits
 * without consulting the payload, and threading a parsed object through here
 * would move that decision to the table.
 */
export interface ModelAssistState {
  providers: readonly { id: string; egress: "local" | "remote" }[];
  explanations: ReadonlyMap<string, unknown>;
  /**
   * Keyed by finding id. Typed, unlike `explanations`, because a drafted-fix
   * result is an envelope our own API builds rather than a payload a provider
   * chose -- but threaded just as untouched. Nothing here reads `patch`.
   */
  draftedFixes: ReadonlyMap<string, DraftedFixResult>;
  /** Keyed by ARTIFACT id: the decision is about the artifact, not the finding. */
  decisions: ReadonlyMap<string, "accepted" | "rejected">;
  decisionPending: ReadonlySet<string>;
  pending: ReadonlySet<string>;
  messages: ReadonlyMap<string, string>;
  remediations?: ReadonlyMap<string, RemediationProposal>;
  remediationPending?: ReadonlySet<string>;
  remediationMessages?: ReadonlyMap<string, string>;
  onExplain: (findingId: string, input: ModelAssistInput) => void;
  onDraftFix: (findingId: string, input: ModelAssistInput) => void;
  onAutoRemediate?: (findingId: string) => void;
  onDecideArtifact: (
    findingId: string,
    artifactId: string,
    decision: "accepted" | "rejected",
  ) => void;
}

export function FindingsTable({
  findings,
  queryState,
  visibleColumns,
  selected,
  onSort,
  onToggleSelected,
  onTogglePageSelected,
  onOpenStory,
  focusedId,
  assignees = [],
  assignmentPendingId,
  onAssign,
}: {
  findings: FindingView[];
  queryState: FindingQueryState;
  visibleColumns: ReadonlySet<ColumnKey>;
  selected: ReadonlySet<string>;
  onSort: (sort: FindingSortKey) => void;
  onToggleSelected: (id: string) => void;
  onTogglePageSelected: () => void;
  onOpenStory: (id: string) => void;
  focusedId?: string | undefined;
  assignees?: readonly FindingAssigneeOption[] | undefined;
  assignmentPendingId?: string | null | undefined;
  onAssign?: ((finding: FindingView, assigneeId: string | null) => void) | undefined;
}) {
  const focusedRowRef = useRef<HTMLTableRowElement>(null);
  // The row ref points at the newly focused row after React commits the prop change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: focusedId is the scroll trigger.
  useEffect(() => {
    focusedRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [focusedId]);
  const pageSelected = findings.length > 0 && findings.every((finding) => selected.has(finding.id));
  const pageSelectionCount = findings.filter((finding) => selected.has(finding.id)).length;

  return (
    <section className="table-scroll findings-table-container" aria-label="Findings table">
      <table className="data-table findings-table">
        <thead>
          <tr>
            <th className="column-select">
              <SelectionCheckbox
                checked={pageSelected}
                mixed={pageSelectionCount > 0 && !pageSelected}
                label="Select all findings on this page"
                onChange={onTogglePageSelected}
              />
            </th>
            <SortHeader label="Finding" sortKey="default" state={queryState} onSort={onSort} />
            {visibleColumns.has("severity") ? (
              <SortHeader
                label="Severity"
                sortKey="severity"
                state={queryState}
                onSort={onSort}
                className="column-severity"
              />
            ) : null}
            {visibleColumns.has("status") ? (
              <SortHeader
                label="Status"
                sortKey="status"
                state={queryState}
                onSort={onSort}
                className="column-status"
              />
            ) : null}
            {visibleColumns.has("validation") ? (
              <SortHeader
                label="Verdict"
                sortKey="validationDepth"
                state={queryState}
                onSort={onSort}
                className="column-validation"
              />
            ) : null}
            {visibleColumns.has("repository") ? (
              <th className="column-repository">Repository</th>
            ) : null}
            {visibleColumns.has("path") ? (
              <SortHeader
                label="Location"
                sortKey="path"
                state={queryState}
                onSort={onSort}
                className="column-path"
              />
            ) : null}
            {visibleColumns.has("rule") ? <th className="column-rule">Rule</th> : null}
            {visibleColumns.has("confidence") ? (
              <SortHeader
                label="Confidence"
                sortKey="confidence"
                state={queryState}
                onSort={onSort}
                className="column-confidence"
              />
            ) : null}
            {visibleColumns.has("replay") ? <th className="column-replay">Replay</th> : null}
            {visibleColumns.has("owner") ? <th className="column-owner">Owner / SLA</th> : null}
            {visibleColumns.has("updated") ? (
              <SortHeader
                label="Updated"
                sortKey="updatedAt"
                state={queryState}
                onSort={onSort}
                className="column-updated"
              />
            ) : null}
          </tr>
        </thead>
        <tbody>
          {findings.map((finding) => (
            <FragmentRow
              key={finding.id}
              finding={finding}
              isFocused={focusedId === finding.id}
              focusedRowRef={focusedId === finding.id ? focusedRowRef : undefined}
              isSelected={selected.has(finding.id)}
              visibleColumns={visibleColumns}
              assignees={assignees}
              assignmentPending={assignmentPendingId === finding.id}
              onAssign={onAssign}
              onToggleSelected={() => onToggleSelected(finding.id)}
              onOpenStory={() => onOpenStory(finding.id)}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function FragmentRow({
  finding,
  isFocused = false,
  focusedRowRef,
  isSelected,
  visibleColumns,
  assignees,
  assignmentPending,
  onAssign,
  onToggleSelected,
  onOpenStory,
}: {
  finding: FindingView;
  isFocused?: boolean;
  focusedRowRef?: Ref<HTMLTableRowElement> | undefined;
  isSelected: boolean;
  visibleColumns: ReadonlySet<ColumnKey>;
  assignees: readonly FindingAssigneeOption[];
  assignmentPending: boolean;
  onAssign?: ((finding: FindingView, assigneeId: string | null) => void) | undefined;
  onToggleSelected: () => void;
  onOpenStory: () => void;
}) {
  return (
    <tr
      ref={focusedRowRef}
      data-focused={isFocused ? "true" : undefined}
      className={["data-row finding-data-row", isFocused ? "finding-data-row--focused" : ""]
        .filter(Boolean)
        .join(" ")}
      onClick={(event) => {
        if (!isFindingRowControl(event.target)) onOpenStory();
      }}
    >
      <td className="column-select" data-label="Select">
        <SelectionCheckbox
          checked={isSelected}
          label={`Select ${finding.title}`}
          onChange={onToggleSelected}
        />
      </td>
      <td className="column-finding" data-label="Finding">
        <button className="finding-title-button" type="button" onClick={onOpenStory}>
          <span className="finding-issue-id">{finding.cwe || displayRuleId(finding.ruleId)}</span>
          <span>
            <strong>{finding.title}</strong>
            <small className="finding-title-button__story" title={findingQueueMeta(finding)}>
              {findingQueueMeta(finding)}
            </small>
          </span>
        </button>
      </td>
      {visibleColumns.has("severity") ? (
        <td className="column-severity" data-label="Severity">
          <SeverityBadge severity={finding.severity} />
        </td>
      ) : null}
      {visibleColumns.has("status") ? (
        <td className="column-status" data-label="Status">
          <StatusBadge status={finding.status} />
        </td>
      ) : null}
      {visibleColumns.has("validation") ? (
        <td className="column-validation" data-label="Validation">
          <Badge
            label={`${finding.validationDepth}V`}
            tone={finding.validationDepth === 4 ? "positive" : "neutral"}
          />
        </td>
      ) : null}
      {visibleColumns.has("repository") ? (
        <td
          className="column-repository truncate"
          data-label="Repository"
          title={finding.repositoryName}
        >
          {finding.repositoryName}
        </td>
      ) : null}
      {visibleColumns.has("path") ? (
        <td
          className="column-path"
          data-label="Location"
          title={`${finding.location.path}:${finding.location.lineStart}`}
        >
          <code>
            {finding.location.path}:{finding.location.lineStart}
          </code>
        </td>
      ) : null}
      {visibleColumns.has("rule") ? (
        <td
          className="column-rule"
          data-label="Rule"
          title={`${displayRuleId(finding.ruleId)}@${finding.ruleVersion}`}
        >
          <code>{displayRuleId(finding.ruleId)}</code>
        </td>
      ) : null}
      {visibleColumns.has("confidence") ? (
        <td className="column-confidence tabular" data-label="Confidence">
          {formatPercent(finding.confidence)}
        </td>
      ) : null}
      {visibleColumns.has("replay") ? (
        <td className="column-replay" data-label="Replay">
          <ReplayBadge status={finding.evidence.replayStatus} />
        </td>
      ) : null}
      {visibleColumns.has("owner") ? (
        <td className="column-owner" data-label="Owner / SLA">
          {onAssign ? (
            <FindingAssigneePicker
              finding={finding}
              assignees={assignees}
              pending={assignmentPending}
              onAssign={onAssign}
            />
          ) : finding.assignment ? (
            <span title={`Due ${formatDateTime(finding.assignment.dueAt)}`}>
              <strong>{assigneeDisplayName(finding.assignment.assigneeId)}</strong>
              <small>{formatDateTime(finding.assignment.dueAt)}</small>
            </span>
          ) : (
            <span className="muted">Unassigned</span>
          )}
        </td>
      ) : null}
      {visibleColumns.has("updated") ? (
        <td
          className="column-updated"
          data-label="Updated"
          title={formatDateTime(finding.updatedAt)}
        >
          {formatDateTime(finding.updatedAt)}
        </td>
      ) : null}
    </tr>
  );
}
